// The typed data-access interface the portal screens read through.
//
// Both backends implement the SAME `PortalDataSource` contract:
//   • the fixtures adapter (DEFAULT — keeps the existing tests + UI working with
//     no network), and
//   • the Supabase adapter (selected only when VITE_SUPABASE_URL is set).
//
// The interface is the seam that lets the UI move from mock data to a real,
// RLS-scoped backend without screen changes. It deliberately mirrors the data
// model in docs/revamp/03_BACKEND_AND_PROFESSIONAL_LAYER.md §2 (601 types) and
// the migration schema (622).
//
// PRIVACY NON-NEGOTIABLE encoded in this contract: children come back as a
// `ServerChild` — pseudonymous UUID + owner only. There is NO `displayLabel`
// here because there is ZERO child PII server-side; the owner-set label lives
// on-device and is rendered client-side. Both adapters honour this shape so the
// privacy posture is structural, not incidental.

import type {
  Account,
  Assignment,
  AssignmentStatus,
  Child,
  ChildLink,
  Consent,
  ConsentScopeKey,
  HomeNote,
  Id,
  License,
  LinkRole,
  MemberInvitation,
  Membership,
  MembershipRole,
  Organisation,
  Seat,
  SessionSummary,
  SkillStepState,
  SkillSummary,
  SupportProfile,
} from "../domain/types";
import type { Sponsorship } from "../domain/sponsorship";

/**
 * A child exactly as the backend may know them: a pseudonymous UUID and its
 * owner — and nothing else. The owner-set `displayLabel` carried by the richer
 * domain `Child` is intentionally absent (it stays on-device, rendered
 * client-side). See the `child` table in the migration: id + owner only.
 */
export type ServerChild = Pick<Child, "id" | "ownerAccountId">;

/** Compile-time assurance that `ServerChild` carries no PII-bearing key. */
export type ServerChildKey = keyof ServerChild; // "id" | "ownerAccountId"

// --- Admin aggregate reads ---------------------------------------------------
//
// The admin screens below CANNOT be built from plain table reads: RLS correctly
// forbids an org admin from selecting child / child_link / consent rows (those
// policies are owner + consented-viewer only, and weakening them to serve an
// admin dashboard would be the single worst change we could make to this
// product). They are served instead by admin-only `security definer` aggregate
// RPCs added in migration 20260726000003.
//
// Every child that appears in these shapes is a `childRef`: a stable,
// non-reversible md5 slice scoped to the org — NEVER the child UUID, never a
// label, never a progress figure. An admin sees "six children are supported by
// my staff"; they learn nothing about any one child.

/** Opaque, org-scoped child reference. Not a UUID and not resolvable to one. */
export type ChildRef = string;

/** Counts behind the admin Overview screen. */
export interface OrgOverview {
  orgName: string;
  orgType: string;
  seatsTotal: number;
  seatsUsed: number;
  memberCount: number;
  adminCount: number;
  linkedChildCount: number;
  pendingInviteCount: number;
}

export type SharingStatus = "active" | "pending" | "revoked";

/** One child supported by this org's members, seen only as an opaque ref. */
export interface OrgRosterEntry {
  childRef: ChildRef;
  /** Emails of the ORG MEMBERS supporting them (adult, already admin-visible). */
  supportedBy: string[];
  sharingStatus: SharingStatus;
  /** ISO timestamp of the most recent consent change, or null if never granted. */
  lastChange: string | null;
}

/** One consent grant held by a member of this org. */
export interface OrgConsentEntry {
  consentId: Id;
  childRef: ChildRef;
  memberEmail: string;
  scope: ConsentScopeKey[];
  status: SharingStatus;
  grantedAt: string;
  revokedAt: string | null;
}

/** One member of the org, as the admin Members screen needs them. */
export interface OrgMemberEntry {
  accountId: Id;
  /** Real adult email. Only reachable via the admin-gated RPC — `account`'s RLS
   *  is self-only, so a plain table read returns the admin's own row and nothing
   *  else, which is why the Members list used to show opaque refs. */
  email: string;
  role: MembershipRole;
  /** null when this member holds no seat. */
  seatStatus: "active" | "pending" | null;
  joinedAt: string;
}

export type OrgActivityKind = "member" | "seat" | "invite" | "org";

/** One entry in the org's append-only audit feed. */
export interface OrgActivityEntry {
  id: Id;
  kind: OrgActivityKind;
  text: string;
  at: string;
}


// --- Family licences, billing, kids management, console (migration 0008) ----
//
// Organisations are charged for FAMILY LICENCES only (one sponsored family = one
// licence); staff seats are free. An organisation is a centre or ONE individual
// practitioner, and either may run with no licences at all.

export type OrgKind = "organisation" | "individual";
export type OrgLifecycle = "active" | "suspended" | "closed";

/** The two facts every admin screen needs about the org itself. */
export interface OrgProfile {
  kind: OrgKind;
  status: OrgLifecycle;
}

/** What granting a licence to an email WOULD do (RPC check_family_licence). */
export type FamilyLicenceCheckStatus =
  | "paid"
  | "licensed"
  | "available"
  | "full"
  | "no_allowance"
  | "suspended";

export interface FamilyLicenceCheck {
  status: FamilyLicenceCheckStatus;
  remaining: number;
}

/**
 * RPC org_billing_summary. Money is a plain number in `currency`.
 *
 * Revenue share (migration 0009): the FAMILY pays the ORGANISATION
 * `familyRetailPriceMonthly` (recorded only, never charged by Zooylo); Zooylo
 * invoices the organisation `familyPriceMonthly` per licence-month, plus SST
 * at `sstRate` %. Every `*Amount` here INCLUDES SST, like an invoice total.
 */
export interface BillingSummary {
  currency: string;
  /** Zooylo's share per family licence per month (before SST). */
  familyPriceMonthly: number;
  /** What the organisation charges a family per month. 0 = not recorded. */
  familyRetailPriceMonthly: number;
  /** retail − share, never below 0 (the server computes it). */
  orgShareMonthly: number;
  /** SST percentage added on top of Zooylo's invoice (0 allowed). */
  sstRate: number;
  familyAllowance: number;
  licencesLive: number;
  licencesClaimed: number;
  licencesPending: number;
  monthToDateLicenceMonths: number;
  monthToDateAmount: number;
  projectedMonthAmount: number;
  lastMonthLicenceMonths: number;
  lastMonthAmount: number;
  outstandingAmount: number;
  orgStatus: OrgLifecycle;
  contractStart: string | null;
  contractEnd: string | null;
}

export type InvoiceStatus = "draft" | "issued" | "paid" | "void";

export interface Invoice {
  id: Id;
  orgId: Id;
  number: string;
  periodStart: string;
  periodEnd: string;
  licenceMonths: number;
  unitPrice: number;
  /** Before SST. Pre-0009 invoices carry their amount here. */
  subtotal: number;
  sstRate: number;
  sstAmount: number;
  /** The TOTAL to pay (subtotal + SST). */
  amount: number;
  currency: string;
  status: InvoiceStatus;
  issuedAt: string;
  dueAt: string | null;
  paidAt: string | null;
  note: string | null;
}

export type ChildActivity = "active" | "inactive" | "unknown";
export type ChildLicence = "licensed" | "paid" | "free";

/** RPC org_children — one child as an org admin may see them: a ref + ACTIVITY. */
export interface OrgChildEntry {
  childRef: ChildRef;
  /** Emails of this org's members linked to the child. */
  supportedBy: string[];
  /** The roles those links carry (not paired with the emails). */
  roles: LinkRole[];
  sharingStatus: SharingStatus;
  activity: ChildActivity;
  licence: ChildLicence;
  archived: boolean;
  linkedSince: string | null;
  /** 0012: has the FAMILY shared this child with the organisation itself? Only a
   *  shared child can have staff assigned. Never says anything about play. */
  consent: OrgChildConsent;
  /** 0012: how many of this org's staff are assigned to the child. */
  staffCount: number;
}

export type OrgChildConsent = "shared" | "not_shared";

/** Roles an admin may give an assigned staff member (assign_child_staff). */
export const STAFF_ROLES = ["teacher", "therapist", "caregiver", "other"] as const;
export type StaffRole = (typeof STAFF_ROLES)[number];

/** RPC org_child_staff — one staff member assigned to one child. Adults only. */
export interface OrgChildStaff {
  accountId: Id;
  email: string;
  role: LinkRole | null;
  assignedAt: string | null;
}

/** RPC grant_family_sponsorships_bulk — one row per distinct submitted email. */
export type BulkLicenceStatus = "licensed" | "already_licensed" | "paid" | "invalid" | "full" | "suspended";

export interface BulkLicenceResult {
  email: string;
  status: BulkLicenceStatus;
  sponsorshipId: Id | null;
}

/** RPC child_access — does this child's family have Premium, and from where. */
export interface ChildAccess {
  premium: boolean;
  source: "store" | "licence" | "none";
}

/** RPC org_analytics — counts only. */
export interface OrgAnalytics {
  members: number;
  admins: number;
  childrenLinked: number;
  childrenActive30d: number;
  childrenArchived: number;
  licencesLive: number;
  licencesClaimed: number;
  licencesEnding30d: number;
  suggestions30d: number;
  suggestionsSeen30d: number;
  suggestionsPlayed30d: number;
  /** Oldest first; `month` is "YYYY-MM". */
  licenceMonthsByMonth: { month: string; licenceMonths: number }[];
}

/** RPC console_orgs — one row per organisation, for platform admins. */
export interface ConsoleOrg {
  orgId: Id;
  name: string;
  kind: OrgKind;
  type: string;
  status: OrgLifecycle;
  createdAt: string;
  adminEmails: string[];
  members: number;
  familyAllowance: number;
  licencesLive: number;
  familyPriceMonthly: number;
  currency: string;
  outstanding: number;
}

/** RPC console_access_codes — one org_access_code row. */
export interface AccessCode {
  code: string;
  label: string;
  plan: string;
  kind: OrgKind;
  seatsTotal: number;
  familyAllowance: number;
  familyPriceMonthly: number;
  currency: string;
  maxUses: number;
  uses: number;
  expiresAt: string | null;
  createdAt: string;
}

export interface NewAccessCode {
  label: string;
  kind: OrgKind;
  seats: number | null;
  familyAllowance: number;
  /** Zooylo's share per family licence per month. */
  familyPrice: number;
  /** What the organisation charges the family per month (recorded only). */
  familyRetailPrice: number;
  /** SST % on Zooylo's invoice. */
  sstRate: number;
  currency: string;
  maxUses: number;
  expiresAt: string | null;
}

export interface LicenceTerms {
  seats: number | null;
  familyAllowance: number | null;
  /** Zooylo's share per family licence per month. */
  familyPrice: number | null;
  /** What the organisation charges the family per month (recorded only). */
  familyRetailPrice: number | null;
  /** SST % on Zooylo's invoice. */
  sstRate: number | null;
  currency: string | null;
  billingEmail: string | null;
  /** console_set_licence keeps the current value when this is null. */
  contractEnd: string | null;
}

export interface InvoicePreview {
  licenceMonths: number;
  unitPrice: number;
  subtotal: number;
  sstRate: number;
  sstAmount: number;
  /** The total: subtotal + SST. */
  amount: number;
  currency: string;
}

/**
 * What the notify-family edge function is asked to push to the parent's phone:
 * a new practice suggestion, or a family licence the organisation just gave.
 */
export type NotifyFamilyInput =
  | { kind: "suggestion"; assignmentId: Id }
  /** 1–3 games sent together (create_suggestions): ONE push for the group. */
  | { kind: "suggestions"; batchId: Id }
  | { kind: "licence"; sponsorshipId: Id };

/**
 * RPC child_suggestions — one practice idea (one game) sent for a child, by the
 * caller or by another adult helping that child. The rows of one send share a
 * `batchId`; ideas sent before grouping existed have none. The sender is named
 * only in the words the FAMILY chose for that adult (link label + role) and
 * their organisation — never another adult's email.
 */
export interface ChildSuggestion {
  id: Id;
  batchId: Id | null;
  /** A built game id. */
  target: string;
  note: string | null;
  status: AssignmentStatus;
  seenAt: string | null;
  sentAt: string;
  fromMe: boolean;
  senderRole: LinkRole | null;
  senderLabel: string | null;
  senderOrg: string | null;
}

export interface NewInvoice {
  periodStart: string;
  periodEnd: string;
  /** null = licence-months x price, computed by the server. */
  amount: number | null;
  due: string | null;
  note: string | null;
}

/** RPC child_adults (OWNER ONLY) — every adult linked to one of their children. */
export interface ChildAdult {
  accountId: Id;
  email: string;
  role: LinkRole | null;
  label: string | null;
  scope: ConsentScopeKey[];
  status: SharingStatus;
  orgName: string | null;
  linkedAt: string | null;
}


/**
 * The typed data interface every screen reads through. Every method is async so
 * the same contract fits both an in-memory adapter (resolves immediately) and a
 * network adapter (Supabase). Results are already access-scoped: the Supabase
 * adapter relies on Row Level Security to return only the caller's permitted
 * rows, so callers must never assume they can see the whole org/table.
 */
export interface PortalDataSource {
  organisations(): Promise<Organisation[]>;
  accounts(): Promise<Account[]>;
  memberships(): Promise<Membership[]>;
  /** Pseudonymous children visible to the caller (no PII — see {@link ServerChild}). */
  children(): Promise<ServerChild[]>;
  childLinks(): Promise<ChildLink[]>;
  consents(): Promise<Consent[]>;
  sessionSummaries(): Promise<SessionSummary[]>;
  skillSummaries(): Promise<SkillSummary[]>;
  assignments(): Promise<Assignment[]>;
  /** Skill-step snapshot rows (RLS: app_can_view_child(child,'progress')). */
  stepStates(): Promise<SkillStepState[]>;
  /** "How best to support me" rows (RLS: app_can_view_child(child,'progress')). */
  supportProfiles(): Promise<SupportProfile[]>;
  licenses(): Promise<License[]>;
  seats(): Promise<Seat[]>;
  /** Pending member invitations visible to the caller (RLS: org admins only). */
  memberInvitations(): Promise<MemberInvitation[]>;

  // --- Writes ---------------------------------------------------------------
  //
  // Every write is RLS/authorization-gated server-side and therefore requires a
  // signed-in session. The Supabase adapter routes the privileged ones through
  // `security definer` RPCs (the same the mobile app calls) and the rest through
  // RLS-permitted table writes; the fixtures adapter simulates them offline so
  // the UI + tests keep working with no backend. Failures throw
  // {@link PortalWriteError}, which carries the Postgres SQLSTATE `code` so the
  // UI can show calm, specific copy (42501 not-authorized, P0001 rule violation,
  // P0002 not-found) instead of parsing message text.

  /** Owner mints a single-use link code for a child. RPC: generate_link_code. */
  generateLinkCode(childId: Id, scope?: ConsentScopeKey[]): Promise<string>;
  /** Any signed-in adult claims a code → pending viewer link. RPC: claim_link_code. */
  claimLinkCode(code: string): Promise<Id>;
  /** Owner grants a viewer the given scope. RPC: grant_consent. Returns consent id. */
  grantConsent(
    childId: Id,
    viewerAccountId: Id,
    scope: ConsentScopeKey[],
  ): Promise<Id>;
  /** Owner narrows/removes a viewer's scope (empty = revoke all). RPC: revoke_consent. */
  revokeConsent(
    childId: Id,
    viewerAccountId: Id,
    scope: ConsentScopeKey[],
  ): Promise<void>;
  /** Owner cuts a viewer entirely and purges their shared copies. RPC: purge_shared_data. */
  purgeSharedData(childId: Id, viewerAccountId: Id): Promise<void>;
  /** Admin assigns a seat (atomic vs licence capacity). RPC: allocate_seat. */
  allocateSeat(orgId: Id, accountId: Id): Promise<void>;
  /** Admin frees a seat. RPC: revoke_seat. */
  revokeSeat(orgId: Id, accountId: Id): Promise<void>;
  /** Admin invites an adult by email. RPC: invite_member. Returns invitation id. */
  inviteMember(orgId: Id, email: string, role: MembershipRole): Promise<Id>;

  /**
   * Caregiver sends 1–3 games together with ONE optional note (<= 280 chars).
   * Returns the batch id, which `notifyFamily({ kind: "suggestions" })` takes.
   * RPC: create_suggestions (P0001 on 0 / >3 games or a long note; 42501 when
   * the family has not shared "results" with the caller).
   */
  createSuggestions(childId: Id, targets: readonly string[], note?: string): Promise<Id>;
  /** Every practice idea sent for one child, newest first. RPC: child_suggestions. */
  childSuggestions(childId: Id): Promise<ChildSuggestion[]>;
  /** Admin removes a member: frees their seat, cancels invites, drops membership. RPC: remove_member. */
  removeMember(orgId: Id, accountId: Id): Promise<void>;
  /** Admin cancels a pending invitation. Direct delete (RLS member_invitation_admin_all). */
  cancelInvite(invitationId: Id): Promise<void>;

  // --- Organisation onboarding ---------------------------------------------
  //
  // Without these two the admin portal is unreachable: nobody could create an
  // organisation, and `inviteMember` wrote a row no code path ever consumed.

  /**
   * Creates an organisation, makes the caller its first admin, provisions the
   * licence from the access code's allowance, and seats the creator.
   * RPC: create_organisation. Throws P0002 for an unknown code, P0001 for an
   * expired/spent code or an account that already belongs to an org.
   */
  createOrganisation(
    name: string,
    type: string,
    accessCode: string,
  ): Promise<Id>;

  /**
   * Turns every valid invitation addressed to the caller's email into a real
   * membership (+ a seat when the licence has room). Called once on every admin
   * sign-in — that is what makes an invite land without an invitation email.
   * RPC: accept_member_invitations. Returns how many were accepted.
   */
  acceptMemberInvitations(): Promise<number>;

  /** Admin promotes/demotes a member. RPC: set_member_role. Refuses to remove the last admin. */
  setMemberRole(orgId: Id, accountId: Id, role: MembershipRole): Promise<void>;

  /** Admin renames the org / changes its type. RPC: update_organisation. */
  updateOrganisation(orgId: Id, name: string, type: string): Promise<void>;

  // --- Admin aggregate reads (see the block above ServerChild) --------------

  /** Counts for the Overview screen. RPC: org_overview. Null when the org is gone. */
  orgOverview(orgId: Id): Promise<OrgOverview | null>;
  /** Members + their seats + real emails, in one call. RPC: org_members. */
  orgMembers(orgId: Id): Promise<OrgMemberEntry[]>;
  /** Roster oversight rows — opaque child refs only. RPC: org_roster_summary. */
  orgRoster(orgId: Id): Promise<OrgRosterEntry[]>;
  /** Consent audit trail — opaque child refs only. RPC: org_consent_log. */
  orgConsentLog(orgId: Id): Promise<OrgConsentEntry[]>;
  /** Recent org activity, newest first. Direct select (RLS org_activity_select_members). */
  orgActivity(orgId: Id, limit?: number): Promise<OrgActivityEntry[]>;

  // --- Family sponsorship ---------------------------------------------------
  //
  // An organisation grants a FAMILY it serves full app access on the org's
  // licence. Keyed on the parent's ADULT EMAIL and attached to their account the
  // first time they sign in — see the migration header for why it is email-keyed
  // and not a redeemable code.
  //
  // Sponsorship is account-to-org and touches NO child: these shapes carry an
  // adult email and dates, and nothing else. Families draw on
  // `license.family_allowance`, which is deliberately separate from the staff
  // `seats_total` so a centre's own therapists can never be squeezed out.
  //
  // Reads go direct (RLS `family_sponsorship_admin_read` scopes them to the
  // caller's own org); every WRITE goes through a `security definer` RPC,
  // because the table has no insert/update policy at all — a direct client
  // insert would sail straight past the allowance check.

  /** Every grant this org has made, newest first. RLS: org admins only. */
  listSponsorships(orgId: Id): Promise<Sponsorship[]>;
  /**
   * How many families this org's licence permits at once
   * (`license.family_allowance`). Defaults to 0 server-side, and 0 means
   * sponsoring is refused — a first-class state, not an error.
   */
  getFamilyAllowance(orgId: Id): Promise<number>;
  /**
   * Admin sponsors a family by adult email until `endsAt` (ISO-8601).
   * RPC: grant_family_sponsorship. Returns the new grant's id. Throws P0001 for
   * an invalid email, a past end date, a licence with no allowance, an allowance
   * already reached, or a family that is already sponsored.
   */
  grantSponsorship(orgId: Id, email: string, endsAt: string): Promise<Id>;
  /**
   * Admin ends a sponsorship early. RPC: revoke_family_sponsorship. The row is
   * kept as an audit trail, never deleted, and revoking twice is a no-op.
   */
  revokeSponsorship(id: Id): Promise<void>;

  // --- Family licences, billing, kids, analytics (migration 0008) -----------

  /** Org kind + lifecycle status. Direct select on organisation. */
  orgProfile(orgId: Id): Promise<OrgProfile>;
  /** What granting would do, before the admin commits. RPC: check_family_licence. */
  checkFamilyLicence(orgId: Id, email: string): Promise<FamilyLicenceCheck>;
  /** Moves a LIVE licence's end date. RPC: extend_family_sponsorship. */
  extendSponsorship(id: Id, endsAt: string): Promise<void>;
  /** RPC: org_billing_summary. Null when the org is gone. */
  orgBillingSummary(orgId: Id): Promise<BillingSummary | null>;
  /** Invoices for one org, newest period first. RLS hides drafts from org admins. */
  listInvoices(orgId: Id): Promise<Invoice[]>;
  /** RPC: org_children — refs + activity, never progress. */
  orgChildren(orgId: Id): Promise<OrgChildEntry[]>;
  /** RPC: set_org_child_archived. */
  setOrgChildArchived(orgId: Id, childRef: ChildRef, archived: boolean): Promise<void>;
  /** RPC: end_org_support. Returns how many links were removed. */
  endOrgSupport(orgId: Id, childRef: ChildRef): Promise<number>;
  /** RPC: org_analytics. */
  orgAnalytics(orgId: Id): Promise<OrgAnalytics>;

  // --- Licence tools, organisation consent + staff (migrations 0012, 0013) --

  /** Up to 2000 emails, one end date. One result per distinct email, in order.
   *  RPC: grant_family_sponsorships_bulk. */
  grantSponsorshipsBulk(orgId: Id, emails: readonly string[], endsAt: string): Promise<BulkLicenceResult[]>;
  /** Moves the end date of several live licences. Returns how many changed.
   *  RPC: set_sponsorship_end_dates (P0001 when it would exceed the allowance). */
  setSponsorshipEndDates(orgId: Id, ids: readonly Id[], endsAt: string): Promise<number>;
  /** Corrects a licence's email. A CLAIMED licence is ended and a new one given
   *  to the right address with the same end date. Returns the live licence id.
   *  RPC: change_sponsorship_email. */
  changeSponsorshipEmail(id: Id, email: string): Promise<Id>;
  /** Staff assigned to one child. RPC: org_child_staff. */
  orgChildStaff(orgId: Id, childRef: ChildRef): Promise<OrgChildStaff[]>;
  /** RPC: assign_child_staff (P0001 when the family has not shared the child). */
  assignChildStaff(orgId: Id, childRef: ChildRef, accountId: Id, role: StaffRole): Promise<void>;
  /** RPC: unassign_child_staff. */
  unassignChildStaff(orgId: Id, childRef: ChildRef, accountId: Id): Promise<void>;
  /** Premium yes/no for a child the caller may see. RPC: child_access. */
  childAccess(childId: Id): Promise<ChildAccess>;

  // --- Owner console (platform admins only) --------------------------------

  /** RPC: am_i_platform_admin. False on any failure. */
  amIPlatformAdmin(): Promise<boolean>;
  consoleOrgs(): Promise<ConsoleOrg[]>;
  consoleAccessCodes(): Promise<AccessCode[]>;
  /** Returns the new code. RPC: console_create_access_code. */
  consoleCreateAccessCode(input: NewAccessCode): Promise<string>;
  /** The licence row's current terms (RLS license_select_platform). */
  consoleLicenceTerms(orgId: Id): Promise<LicenceTerms>;
  consoleSetLicence(orgId: Id, terms: LicenceTerms): Promise<void>;
  consoleSetOrgStatus(orgId: Id, status: OrgLifecycle, endLicences: boolean): Promise<void>;
  /** Returns the new invoice id. RPC: console_issue_invoice. */
  /** What console_issue_invoice would charge for a period. RPC: console_preview_invoice. */
  consolePreviewInvoice(orgId: Id, periodStart: string, periodEnd: string): Promise<InvoicePreview | null>;
  consoleIssueInvoice(orgId: Id, input: NewInvoice): Promise<Id>;
  consoleSetInvoiceStatus(invoiceId: Id, status: InvoiceStatus): Promise<void>;

  // --- Push (migration 0009) ------------------------------------------------

  /**
   * Asks the notify-family edge function to push to the parent's phone.
   * Rejects on failure; callers use `notifyFamilyInBackground`, which never
   * blocks or fails the write it follows. Fixtures: a no-op.
   */
  notifyFamily(input: NotifyFamilyInput): Promise<void>;

  // --- Per-child linking + "Seen at home" ----------------------------------

  /**
   * Owner sets how a linked adult appears, or a viewer sets the label on their
   * OWN link. `role` null keeps the role; `label` "" clears it, null keeps it.
   * RPC: set_link_details.
   */
  setLinkDetails(
    childId: Id,
    viewerAccountId: Id,
    role: LinkRole | null,
    label: string | null,
  ): Promise<void>;
  /** OWNER ONLY: every adult linked to one child. RPC: child_adults. */
  childAdults(childId: Id): Promise<ChildAdult[]>;
  /** "Seen at home" notes visible to the caller (RLS: owner, or shared with them). */
  homeNotes(): Promise<HomeNote[]>;
}

export type {
  Account,
  Assignment,
  ChildLink,
  Consent,
  ConsentScopeKey,
  Id,
  License,
  LinkRole,
  MemberInvitation,
  Membership,
  MembershipRole,
  Organisation,
  Seat,
  SessionSummary,
  SkillStepState,
  SkillSummary,
  SupportProfile,
};
export type { Sponsorship, HomeNote };
